import { Injectable } from "@angular/core";

import { CryptoService } from "@bitwarden/common/abstractions/crypto.service";
import { StateService } from "@bitwarden/common/abstractions/state.service";
import { EncString } from "@bitwarden/common/models/domain/enc-string";

import { DeviceTrustService } from "./device-trust.service";

/**
 * This browser has no stored device keypair, so it can't open a UserKey wrapped for this
 * device (e.g. IndexedDB was cleared while the server still trusts this device). Distinct
 * from an unwrap failure so callers can route to re-approval instead of a generic error.
 */
export class DeviceNotTrustedLocallyError extends Error {
  constructor() {
    super("This device has no local device key; it is not trusted locally");
    this.name = "DeviceNotTrustedLocallyError";
  }
}

/**
 * TDE returning-device unlock (GOOGLE_SSO_PLAN.md, "Fix plan" under the known
 * single-session limitation). Mirror image of `FirstDeviceSetupService`: instead of
 * generating a UserKey and wrapping it for the first time, this unwraps a UserKey the
 * backend already has on file for this exact device and unlocks with it — no UI, no
 * network call of its own beyond what login already did.
 */
@Injectable({
  providedIn: "root",
})
export class ReturningDeviceUnlockService {
  constructor(
    private deviceTrustService: DeviceTrustService,
    private cryptoService: CryptoService,
    private stateService: StateService,
  ) {}

  /**
   * @param wrappedUserKey This device's UserKey, wrapped under this device's own public
   * key by the backend (or, self-approval case, by this device itself) — an EncString-
   * formatted string (`AuthResult.encryptedUserKey`).
   * @param encryptedPrivateKey The account's RSA private key, encrypted under the UserKey
   * (`AuthResult.privateKey`) — set alongside the unwrapped UserKey so the rest of the app
   * can decrypt it the same way it would after any other unlock.
   */
  async unlockWithLocalDeviceKey(
    wrappedUserKey: string,
    encryptedPrivateKey: string,
  ): Promise<void> {
    // Get-only: generating a fresh keypair here would overwrite the one the server
    // registered and could never open `wrappedUserKey`.
    const keyPair = await this.deviceTrustService.getDeviceKeyPair();
    if (keyPair == null) {
      throw new DeviceNotTrustedLocallyError();
    }
    const userKey = await this.deviceTrustService.unwrapUserKey(
      new EncString(wrappedUserKey),
      keyPair.privateKey,
    );

    await this.cryptoService.setKey(userKey);
    if (encryptedPrivateKey != null) {
      await this.cryptoService.setEncPrivateKey(encryptedPrivateKey);
    }
    await this.deviceTrustService.markTrustedFor(await this.stateService.getUserId());
  }
}
